/**
 * DeliveryDrawer Component
 * Create / Edit Delivery — large drawer (multi-section)
 * BuilderBel Construction Project Management System
 */

import { ChevronDown, ChevronUp, Plus, Trash2, X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import {
  DatePicker,
  Dropdown,
  FormSwitch,
  Input,
  NumberInput,
  PhoneInput,
  TextArea,
  TimePicker,
} from '../forms';
import type {
  CreateDeliveryRequest,
  Delivery,
  DeliveryItem,
  DeliverySourceType,
  DeliveryTimeWindow,
  WeightUnit,
} from '../../types/delivery';
import { useCreateDeliveryMutation, useUpdateDeliveryMutation } from '../../services/deliveryApi';
import { useToast } from '../../contexts/ToastContext';

// ============================================================================
// CONSTANTS
// ============================================================================

const SOURCE_TYPE_OPTIONS = [
  { value: 'purchase_order', label: 'Purchase Order' },
  { value: 'rental', label: 'Rental' },
  { value: 'equipment', label: 'Equipment' },
  { value: 'other', label: 'Other' },
];

const WEIGHT_UNIT_OPTIONS = [
  { value: 'lbs', label: 'lbs' },
  { value: 'tons', label: 'tons' },
  { value: 'kg', label: 'kg' },
];

const ITEM_STATUS_OPTIONS = [
  { value: 'pending', label: 'Pending' },
  { value: 'received', label: 'Received' },
  { value: 'partial', label: 'Partial' },
  { value: 'damaged', label: 'Damaged' },
  { value: 'missing', label: 'Missing' },
];

// ============================================================================
// HELPERS
// ============================================================================

const formatDateForInput = (dateStr?: string): string => {
  if (!dateStr) return new Date().toISOString().split('T')[0];
  return new Date(dateStr).toISOString().split('T')[0];
};

const emptyItem = (): Omit<DeliveryItem, '_id'> => ({
  description: '',
  material: '',
  orderedQuantity: 1,
  expectedQuantity: 1,
  receivedQuantity: undefined,
  damagedQuantity: undefined,
  unit: 'EA',
  status: 'pending',
  storageLocation: '',
  verificationNotes: '',
});

// ============================================================================
// PROPS
// ============================================================================

interface DeliveryDrawerProps {
  isOpen: boolean;
  onClose: () => void;
  projectId: string;
  delivery?: Delivery | null;
}

// ============================================================================
// COMPONENT
// ============================================================================

const DeliveryDrawer: React.FC<DeliveryDrawerProps> = ({
  isOpen,
  onClose,
  projectId,
  delivery,
}) => {
  const toast = useToast();
  const isEditing = !!delivery;

  // Mutations
  const [createDelivery, { isLoading: isCreating }] = useCreateDeliveryMutation();
  const [updateDelivery, { isLoading: isUpdating }] = useUpdateDeliveryMutation();
  const isSaving = isCreating || isUpdating;

  // ---- Basic Fields ----
  const [title, setTitle] = useState<string>(delivery?.title || '');
  const [description, setDescription] = useState<string>(delivery?.description || '');
  const [sourceType, setSourceType] = useState<DeliverySourceType>(
    delivery?.sourceType || 'purchase_order'
  );
  const [sourceNumber, setSourceNumber] = useState<string>(delivery?.sourceNumber || '');
  const [requestedDate, setRequestedDate] = useState<string>(
    formatDateForInput(delivery?.requestedDate)
  );
  const [requestedWindowStart, setRequestedWindowStart] = useState<string>(
    delivery?.requestedWindow?.startTime || ''
  );
  const [requestedWindowEnd, setRequestedWindowEnd] = useState<string>(
    delivery?.requestedWindow?.endTime || ''
  );

  // ---- Vendor ----
  const [vendorName, setVendorName] = useState<string>(delivery?.vendorName || '');
  const [vendorContact, setVendorContact] = useState<string>(delivery?.vendorContact || '');
  const [vendorPhone, setVendorPhone] = useState<string>(delivery?.vendorPhone || '');
  const [vendorEmail, setVendorEmail] = useState<string>(delivery?.vendorEmail || '');
  const [carrier, setCarrier] = useState<string>(delivery?.carrier || '');
  const [trackingNumber, setTrackingNumber] = useState<string>(delivery?.trackingNumber || '');

  // ---- Location / Contact ----
  const [deliveryLocation, setDeliveryLocation] = useState<string>(
    delivery?.deliveryLocation || ''
  );
  const [deliveryInstructions, setDeliveryInstructions] = useState<string>(
    delivery?.deliveryInstructions || ''
  );
  const [storageLocation, setStorageLocation] = useState<string>(
    delivery?.storageLocation || ''
  );
  const [liftingRequired, setLiftingRequired] = useState<boolean>(
    delivery?.liftingRequired ?? false
  );
  const [siteContactName, setSiteContactName] = useState<string>(
    delivery?.siteContactName || ''
  );
  const [siteContactPhone, setSiteContactPhone] = useState<string>(
    delivery?.siteContactPhone || ''
  );

  // ---- Items ----
  const [items, setItems] = useState<Omit<DeliveryItem, '_id'>[]>(
    delivery?.items?.length
      ? delivery.items.map(({ _id, ...rest }) => rest)
      : []
  );

  // ---- Totals ----
  const [totalWeight, setTotalWeight] = useState<number | undefined>(delivery?.totalWeight);
  const [weightUnit, setWeightUnit] = useState<WeightUnit>(delivery?.weightUnit || 'lbs');
  const [totalPieces, setTotalPieces] = useState<number | undefined>(delivery?.totalPieces);

  // ---- Notes ----
  const [notes, setNotes] = useState<string>(delivery?.notes || '');

  // ---- Collapsed Sections ----
  const [showCarrier, setShowCarrier] = useState(false);
  const [showItems, setShowItems] = useState(false);
  const [showTotals, setShowTotals] = useState(false);

  // ---- Errors ----
  const [errors, setErrors] = useState<Record<string, string>>({});

  // Reset form when delivery changes or drawer opens
  useEffect(() => {
    if (isOpen) {
      setTitle(delivery?.title || '');
      setDescription(delivery?.description || '');
      setSourceType(delivery?.sourceType || 'purchase_order');
      setSourceNumber(delivery?.sourceNumber || '');
      setRequestedDate(formatDateForInput(delivery?.requestedDate));
      setRequestedWindowStart(delivery?.requestedWindow?.startTime || '');
      setRequestedWindowEnd(delivery?.requestedWindow?.endTime || '');
      setVendorName(delivery?.vendorName || '');
      setVendorContact(delivery?.vendorContact || '');
      setVendorPhone(delivery?.vendorPhone || '');
      setVendorEmail(delivery?.vendorEmail || '');
      setCarrier(delivery?.carrier || '');
      setTrackingNumber(delivery?.trackingNumber || '');
      setDeliveryLocation(delivery?.deliveryLocation || '');
      setDeliveryInstructions(delivery?.deliveryInstructions || '');
      setStorageLocation(delivery?.storageLocation || '');
      setLiftingRequired(delivery?.liftingRequired ?? false);
      setSiteContactName(delivery?.siteContactName || '');
      setSiteContactPhone(delivery?.siteContactPhone || '');
      setItems(
        delivery?.items?.length
          ? delivery.items.map(({ _id, ...rest }) => rest)
          : []
      );
      setTotalWeight(delivery?.totalWeight);
      setWeightUnit(delivery?.weightUnit || 'lbs');
      setTotalPieces(delivery?.totalPieces);
      setNotes(delivery?.notes || '');
      setErrors({});
      setShowCarrier(false);
      setShowItems(false);
      setShowTotals(false);
    }
  }, [isOpen, delivery]);

  // --------------------------------------------------------------------------
  // VALIDATION
  // --------------------------------------------------------------------------

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};
    if (!title.trim()) newErrors.title = 'Title is required';
    if (!vendorName.trim()) newErrors.vendorName = 'Vendor name is required';
    if (!requestedDate) newErrors.requestedDate = 'Requested date is required';
    if (!deliveryLocation.trim()) newErrors.deliveryLocation = 'Delivery location is required';

    // Item validation
    items.forEach((item, idx) => {
      if (!item.description.trim()) {
        newErrors[`item_${idx}_description`] = `Item ${idx + 1}: description is required`;
      }
      if (!item.unit.trim()) {
        newErrors[`item_${idx}_unit`] = `Item ${idx + 1}: unit is required`;
      }
    });

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // --------------------------------------------------------------------------
  // SUBMIT
  // --------------------------------------------------------------------------

  const handleSubmit = async () => {
    if (!validate()) return;

    const requestedWindow: DeliveryTimeWindow | undefined =
      requestedWindowStart && requestedWindowEnd
        ? { startTime: requestedWindowStart, endTime: requestedWindowEnd }
        : undefined;

    const payload: CreateDeliveryRequest = {
      title: title.trim(),
      description: description.trim() || undefined,
      sourceType,
      sourceNumber: sourceNumber.trim() || undefined,
      vendorName: vendorName.trim(),
      vendorContact: vendorContact.trim() || undefined,
      vendorPhone: vendorPhone.trim() || undefined,
      vendorEmail: vendorEmail.trim() || undefined,
      carrier: carrier.trim() || undefined,
      trackingNumber: trackingNumber.trim() || undefined,
      requestedDate,
      requestedWindow,
      deliveryLocation: deliveryLocation.trim(),
      deliveryInstructions: deliveryInstructions.trim() || undefined,
      storageLocation: storageLocation.trim() || undefined,
      liftingRequired,
      siteContactName: siteContactName.trim() || undefined,
      siteContactPhone: siteContactPhone.trim() || undefined,
      items: items.filter(i => i.description.trim()),
      totalWeight: totalWeight ?? undefined,
      weightUnit: totalWeight !== undefined ? weightUnit : undefined,
      totalPieces: totalPieces ?? undefined,
      notes: notes.trim() || undefined,
    };

    try {
      if (isEditing && delivery) {
        await updateDelivery({ projectId, deliveryId: delivery._id, data: payload }).unwrap();
        toast.success('Delivery updated successfully');
      } else {
        await createDelivery({ projectId, data: payload }).unwrap();
        toast.success('Delivery scheduled successfully');
      }
      onClose();
    } catch {
      toast.error(isEditing ? 'Failed to update delivery' : 'Failed to schedule delivery');
    }
  };

  // --------------------------------------------------------------------------
  // ITEMS HANDLERS
  // --------------------------------------------------------------------------

  const addItem = () => setItems(prev => [...prev, emptyItem()]);
  const removeItem = (idx: number) => setItems(prev => prev.filter((_, i) => i !== idx));
  const updateItem = (
    idx: number,
    key: keyof Omit<DeliveryItem, '_id'>,
    val: string | number | undefined
  ) => {
    setItems(prev => prev.map((item, i) => (i === idx ? { ...item, [key]: val } : item)));
  };

  // --------------------------------------------------------------------------
  // RENDER
  // --------------------------------------------------------------------------

  if (!isOpen) return null;

  const content = (
    <>
      {/* Backdrop */}
      <div
        className="fixed inset-0 bg-black/30 backdrop-blur-sm z-[90]"
        onClick={onClose}
      />

      {/* Drawer - large (multi-section) */}
      <div className="fixed inset-6 z-[100] animate-in slide-in-from-right duration-200">
        <div className="bg-white rounded-2xl shadow-2xl h-full flex flex-col">
          {/* Header */}
          <div className="flex items-center justify-between px-8 py-5 border-b border-gray-200 flex-shrink-0">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">
                {isEditing ? 'Edit Delivery' : 'Schedule Delivery'}
              </h2>
              <p className="text-sm text-gray-500 mt-0.5">
                {isEditing
                  ? 'Update delivery details'
                  : 'Schedule a new material or equipment delivery'}
              </p>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
              aria-label="Close drawer"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Scrollable Content */}
          <div className="flex-1 overflow-y-auto px-8 py-6 space-y-6">

            {/* ---- BASIC INFO ---- */}
            <div className="space-y-4">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Basic Information
              </h3>

              <Input
                label="Delivery Title *"
                value={title}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setTitle(e.target.value)}
                placeholder="e.g. Structural Steel Delivery - Phase 1"
                error={errors.title}
              />

              <div className="grid grid-cols-2 gap-4">
                <Dropdown
                  label="Source Type *"
                  value={sourceType}
                  onChange={(val: string) => setSourceType(val as DeliverySourceType)}
                  options={SOURCE_TYPE_OPTIONS}
                />
                <Input
                  label="Source / PO Number"
                  value={sourceNumber}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSourceNumber(e.target.value)
                  }
                  placeholder="e.g. PO-2024-0045"
                />
              </div>

              <TextArea
                label="Description"
                value={description}
                onChange={(val: string) => setDescription(val)}
                placeholder="Additional details about this delivery..."
                rows={2}
              />
            </div>

            {/* ---- SCHEDULE ---- */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Schedule
              </h3>

              <DatePicker
                label="Requested Date"
                value={requestedDate}
                onChange={(date: string | null) => setRequestedDate(date || '')}
                required
                error={errors.requestedDate}
              />

              <div className="grid grid-cols-2 gap-4">
                <TimePicker
                  label="Window Start"
                  value={requestedWindowStart}
                  onChange={time => setRequestedWindowStart(time)}
                  placeholder="07:00"
                />
                <TimePicker
                  label="Window End"
                  value={requestedWindowEnd}
                  onChange={time => setRequestedWindowEnd(time)}
                  placeholder="09:00"
                />
              </div>
            </div>

            {/* ---- VENDOR ---- */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Vendor
              </h3>

              <Input
                label="Vendor Name *"
                value={vendorName}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setVendorName(e.target.value)
                }
                placeholder="e.g. ABC Steel Supply"
                error={errors.vendorName}
              />

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Vendor Contact"
                  value={vendorContact}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setVendorContact(e.target.value)
                  }
                  placeholder="Contact name"
                />
                <PhoneInput
                  label="Vendor Phone"
                  value={vendorPhone}
                  onChange={value => setVendorPhone(value)}
                />
              </div>

              <Input
                label="Vendor Email"
                value={vendorEmail}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setVendorEmail(e.target.value)
                }
                placeholder="vendor@example.com"
              />
            </div>

            {/* ---- CARRIER (collapsible) ---- */}
            <div className="pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowCarrier(v => !v)}
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
              >
                <span>Carrier / Tracking</span>
                {showCarrier ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showCarrier && (
                <div className="grid grid-cols-2 gap-4">
                  <Input
                    label="Carrier"
                    value={carrier}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setCarrier(e.target.value)
                    }
                    placeholder="e.g. ABC Trucking"
                  />
                  <Input
                    label="Tracking Number"
                    value={trackingNumber}
                    onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                      setTrackingNumber(e.target.value)
                    }
                    placeholder="e.g. TRK123456"
                  />
                </div>
              )}
            </div>

            {/* ---- LOCATION ---- */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Delivery Location
              </h3>

              <Input
                label="Delivery Location *"
                value={deliveryLocation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setDeliveryLocation(e.target.value)
                }
                placeholder="e.g. Loading Dock A"
                error={errors.deliveryLocation}
              />

              <Input
                label="Storage Location"
                value={storageLocation}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                  setStorageLocation(e.target.value)
                }
                placeholder="e.g. Laydown Area B"
              />

              <TextArea
                label="Delivery Instructions"
                value={deliveryInstructions}
                onChange={(val: string) => setDeliveryInstructions(val)}
                placeholder="Special instructions for unloading, access, etc."
                rows={2}
              />

              <FormSwitch
                label="Lifting Equipment Required"
                checked={liftingRequired}
                onChange={(checked: boolean) => setLiftingRequired(checked)}
              />
            </div>

            {/* ---- SITE CONTACT ---- */}
            <div className="space-y-4 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Site Contact
              </h3>

              <div className="grid grid-cols-2 gap-4">
                <Input
                  label="Contact Name"
                  value={siteContactName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                    setSiteContactName(e.target.value)
                  }
                  placeholder="e.g. John Smith"
                />
                <PhoneInput
                  label="Contact Phone"
                  value={siteContactPhone}
                  onChange={value => setSiteContactPhone(value)}
                />
              </div>
            </div>

            {/* ---- ITEMS (collapsible) ---- */}
            <div className="pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowItems(v => !v)}
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
              >
                <span>Delivery Items ({items.length})</span>
                {showItems ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showItems && (
                <div className="space-y-4">
                  {items.map((item, idx) => (
                    <div
                      key={idx}
                      className="border border-gray-200 rounded-xl p-4 space-y-3 bg-gray-50"
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-medium text-gray-700">
                          Item {idx + 1}
                        </span>
                        <button
                          type="button"
                          onClick={() => removeItem(idx)}
                          className="p-1 text-red-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </div>

                      <Input
                        label="Description *"
                        value={item.description}
                        onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                          updateItem(idx, 'description', e.target.value)
                        }
                        placeholder="e.g. W14x30 Steel Beam"
                        error={errors[`item_${idx}_description`]}
                      />

                      <div className="grid grid-cols-3 gap-3">
                        <NumberInput
                          label="Ordered Qty *"
                          value={item.orderedQuantity}
                          onChange={(val: number) => updateItem(idx, 'orderedQuantity', val)}
                          min={0}
                        />
                        <NumberInput
                          label="Expected Qty *"
                          value={item.expectedQuantity}
                          onChange={(val: number) => updateItem(idx, 'expectedQuantity', val)}
                          min={0}
                        />
                        <Input
                          label="Unit *"
                          value={item.unit}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateItem(idx, 'unit', e.target.value)
                          }
                          placeholder="EA"
                          error={errors[`item_${idx}_unit`]}
                        />
                      </div>

                      <div className="grid grid-cols-2 gap-3">
                        <Input
                          label="Material"
                          value={item.material || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateItem(idx, 'material', e.target.value)
                          }
                          placeholder="Material type"
                        />
                        <Input
                          label="Storage Location"
                          value={item.storageLocation || ''}
                          onChange={(e: React.ChangeEvent<HTMLInputElement>) =>
                            updateItem(idx, 'storageLocation', e.target.value)
                          }
                          placeholder="Storage area"
                        />
                      </div>

                      {isEditing && (
                        <Dropdown
                          label="Status"
                          value={item.status}
                          onChange={(val: string) =>
                            updateItem(idx, 'status', val)
                          }
                          options={ITEM_STATUS_OPTIONS}
                        />
                      )}
                    </div>
                  ))}

                  <button
                    type="button"
                    onClick={addItem}
                    className="flex items-center space-x-2 text-sm text-brand-600 hover:text-brand-700 font-medium py-2"
                  >
                    <Plus className="h-4 w-4" />
                    <span>Add Item</span>
                  </button>
                </div>
              )}

              {!showItems && (
                <button
                  type="button"
                  onClick={() => { setShowItems(true); addItem(); }}
                  className="flex items-center space-x-2 text-sm text-brand-600 hover:text-brand-700 font-medium py-1"
                >
                  <Plus className="h-4 w-4" />
                  <span>Add Delivery Item</span>
                </button>
              )}
            </div>

            {/* ---- TOTALS (collapsible) ---- */}
            <div className="pt-4 border-t border-gray-100">
              <button
                type="button"
                onClick={() => setShowTotals(v => !v)}
                className="flex items-center justify-between w-full text-sm font-semibold text-gray-700 uppercase tracking-wide mb-3"
              >
                <span>Weight &amp; Piece Count</span>
                {showTotals ? (
                  <ChevronUp className="h-4 w-4 text-gray-400" />
                ) : (
                  <ChevronDown className="h-4 w-4 text-gray-400" />
                )}
              </button>

              {showTotals && (
                <div className="grid grid-cols-3 gap-4">
                  <NumberInput
                    label="Total Weight"
                    value={totalWeight}
                    onChange={(val: number) => setTotalWeight(val || undefined)}
                    min={0}
                    placeholder="0"
                  />
                  <Dropdown
                    label="Weight Unit"
                    value={weightUnit}
                    onChange={(val: string) => setWeightUnit(val as WeightUnit)}
                    options={WEIGHT_UNIT_OPTIONS}
                  />
                  <NumberInput
                    label="Total Pieces"
                    value={totalPieces}
                    onChange={(val: number) => setTotalPieces(val || undefined)}
                    min={0}
                    placeholder="0"
                  />
                </div>
              )}
            </div>

            {/* ---- NOTES ---- */}
            <div className="space-y-3 pt-4 border-t border-gray-100">
              <h3 className="text-sm font-semibold text-gray-700 uppercase tracking-wide">
                Notes
              </h3>
              <TextArea
                label="Additional Notes"
                value={notes}
                onChange={(val: string) => setNotes(val)}
                placeholder="Any additional information about this delivery..."
                rows={3}
              />
            </div>
          </div>

          {/* Footer */}
          <div className="border-t border-gray-200 px-8 py-5 bg-gray-50 flex items-center justify-end space-x-3 flex-shrink-0 rounded-b-2xl">
            <button
              type="button"
              onClick={onClose}
              disabled={isSaving}
              className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="button"
              onClick={handleSubmit}
              disabled={isSaving}
              className="px-5 py-2 text-sm font-medium text-white bg-brand-600 rounded-lg hover:bg-brand-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {isSaving
                ? isEditing
                  ? 'Saving...'
                  : 'Scheduling...'
                : isEditing
                ? 'Save Changes'
                : 'Schedule Delivery'}
            </button>
          </div>
        </div>
      </div>
    </>
  );

  return createPortal(content, document.body);
};

export default DeliveryDrawer;
